import angular from "angular";

import { AccountingRowReportData, Datasets, Dates, LegalEntityType, Numeric, PeriodType, PeriodDateKind, Reports } from "@salaxy/core";

import { SessionService, UiHelpers } from "../../services";
import { ConfigurableGridReportControllerBase } from "./ConfigurableGridReportControllerBase";

/**
 * Controller for accounting row based reports.
 */
export class AccountingRowReportController extends ConfigurableGridReportControllerBase<AccountingRowReportData> {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["Datasets", "UiHelpers", "SessionService", "Reports", "$timeout"];

  /** Usecase uri for report templates */
  public usecaseUri = "palkkaus.fi/reports/accountingRowReport";

  /** Current grid options. */
  public gridOptions: any = {
    statusBar: {
      statusPanels: [
        { statusPanel: "agTotalAndFilteredRowCountComponent", key: "totalAndFilter", align: "left" },
        { statusPanel: "agSelectedRowCountComponent", align: "left" },
        { statusPanel: "agAggregationComponent", align: "right" }
      ]
    },
    enableMultiRowDragging: true,
    rowGroupPanelShow: "always",
    pivotPanelShow: "always",
    enableCharts: true,
    enableRangeSelection: true,
    pivotMode: true,
    suppressExpandablePivotGroups: true,

    defaultExportParams: {
      columnGroups: true,
      headerRowHeight: 30,
      rowHeight: 22,
      fontSize: 14,
    },
    defaultColDef: {
      flex: 1,
      minWidth: 200,
      sortable: true,
      filter: true,
      resizable: true,
      floatingFilter: true,
    },
    sideBar: { toolPanels: ["columns", "filters"] },
    rowData: [],
  };

  /** ID for the element containing grid. */
  public gridElmentId = "#dataGrid";

  /** Default templates */
  public defaultTemplates = [
    {
      id: "holidays",
      owner: null,
      label: "Lomapalkkaraportti",
      config: null,
      isReadOnly: true,
      avatar: {
        initials: "LPR",
        entityType: LegalEntityType.Company,
        color: "#009fd8"
      }
    },
    {
      id: "plain",
      owner: null,
      label: "Kaikki sarakkeet",
      config: { isPivotMode: false},
      isReadOnly: true,
      avatar: {
        initials: "Kasa",
        entityType: LegalEntityType.Company,
        color: "#009fd8"
      }
    }
  ];

  private entryCodeGroups = {
    totalType: "Yhteensä",
    incomeType: "Tulolaji"
  };
  private entryCodes = {
    totalPensionWorker: "Työntekijöiden TyEL-maksut",
    totalPensionEmployer: "Työnantajan TyEL-maksu",
    totalPension: "TyEL-maksut",
    totalUnemploymentWorker: "Työntekijöiden työttömyysvakuutusmaksut",
    totalUnemploymentEmployer: "Työnantajan työttömyysvakuutusmaksu",
    totalUnemployment: "Työttömyysvakuutusmaksut",
    totalSocialSecurityEmployer: "Työnantajan sairausvakuutusmaksu",
    totalSocialSecurityWorker: "Työntekijältä erikseen perittävä sairausvakuutusmaksu",
    totalTax: "Ennakonpidätykset",
    totalPalkkaus: "Palkkaus.fi-palkkio",
    totalWorkerPayment: "Työntekijän nettopalkka ja kulukorvaukset",
    totalPayment: "Maksettu asiakasvaratilille",
    totalAccidentInsurance: "Työtapaturma- ja ammattitautivakuutusmaksut",
    totalGroupLifeInsurance: "Ryhmähenkivakuutusmaksut",
    totalAccruedHolidayPay: "Kertyneet lomapalkat",
    totalAccruedHolidaySocialSecurity: "Kertyneet lomapalkkojen sairausvakuutusmaksut",
    totalAccruedHolidayPension: "Kertyneet lomapalkkojen TyEL-maksut",
    totalAccruedHolidayUnemployment: "Kertyneet lomapalkkojen työttömyysvakuutusmaksut (TVR)",
    totalPaidHolidaySalary: "Maksetut loma-ajan palkat",
    totalPaidHolidayBonus: "Maksetut lomarahat",
    totalPaidHolidayCompensation: "Maksetut lomakorvaukset",
    totalPaidHolidaySocialSecurity: "Maksetut lomapalkkojen sairausvakuutusmaksut",
    totalPaidHolidayPension: "Maksetut lomapalkkojen TyEL-maksut",
    totalPaidHolidayUnemployment: "Maksetut lomapalkkojen työttömyysvakuutusmaksut (TVR)",
    totalGrossSalary: "Bruttopalkka",
  };

  constructor(datasets: Datasets, uiHelpers: UiHelpers, sessionService: SessionService, private reports: Reports,  private $timeout: angular.ITimeoutService) {
    super(datasets, uiHelpers, sessionService);
  }


  /**
   * Initialize controller values.
   */
  public $onInit() {
    super.$onInit();
  }

  /**
   * Processes and sets the data.
   */
  public setData(result: AccountingRowReportData) {
    this.data = result;

    const sortingDateComparator = (a, b) => {
      if (!a || !b) {
        return a === b ? 0 : a ? 1 : -1;
      }
      const mA = Dates.asDate(a);
      const mB = Dates.asDate(b);
      return mA === mB ? 0 : mA > mB ? 1 : -1;
    };
    const filterDateComparator = (a, b) => sortingDateComparator(b, a);

    const columnDefs = [];

    columnDefs.push(...[
      {
        field: "message",
        headerName: "Selite",
        filter: "agMultiColumnFilter",
        enablePivot: true,
        enableRowGroup: true,
      },
      {
        field: "entryDate",
        headerName: "Päiväys",
        valueFormatter: params => Dates.getFormattedDate(params.value),
        type: ["rightAligned"],
        filter: "agDateColumnFilter",
        comparator: sortingDateComparator,
        filterParams: { comparator: filterDateComparator, },
        enablePivot: true,
        enableRowGroup: true,
      },
      {
        field: "amount",
        headerName: "Summa",
        enableValue: true,
        type: ["numericColumn"],
        valueFormatter: params => Numeric.formatNumber(params.value, 2),
        filter: "agNumberColumnFilter",
      },
      {
        field: "dimension.workerName",
        headerName: "Työntekijä",
        filter: "agMultiColumnFilter",
        enablePivot: true,
        enableRowGroup: true,
      },
      /*
      {
        field: "dimension.dimensions.costCenter",
        headerName: "Kustannuspaikka",
        filter: "agMultiColumnFilter",
        enablePivot: true,
        enableRowGroup: true,
      },
      {
        field: "dimension.rowType",
        headerName: "Laskelmarivin tyyppi",
        filter: "agMultiColumnFilter",
        enablePivot: true,
        enableRowGroup: true,
      },
      */
      {
        field: "entryCodeGroup",
        headerName: "Kirjanpitorivin tyyppi",
        filter: "agMultiColumnFilter",
        enablePivot: true,
        enableRowGroup: true,
        valueGetter: params =>  this.getEntryCodeGroupName(params?.data?.entryCodeGroup)
      },
      {
        field: "entryCode",
        headerName: "Kirjanpitorivin tunniste",
        filter: "agMultiColumnFilter",
        enablePivot: true,
        enableRowGroup: true,
        valueGetter: params =>  this.getEntryCodeName(params?.data?.entryCode)
      },
    ]);

    // Create presets if not created.
    const holidays = this.getTemplate("holidays");
    holidays.config = holidays.config ?? {
      isPivotMode: true,
      columnState: this.createHolidaysPreset(columnDefs),
      filterModel: {
        entryCodeGroup: {
          filterType: "multi",
          filterModels: [
            null,
            {
              values: [
                this.entryCodeGroups["totalType"],
              ],
              filterType: "set"
            }
          ]
        },
        entryCode: {
          filterType: "multi",
          filterModels: [
            null,
            {
              values: [
                this.entryCodes["totalGrossSalary"],
                this.entryCodes["totalAccruedHolidayPay"],
                this.entryCodes["totalAccruedHolidaySocialSecurity"],
                this.entryCodes["totalAccruedHolidayPension"],
                this.entryCodes["totalAccruedHolidayUnemployment"],
                this.entryCodes["totalPaidHolidaySalary"],
                this.entryCodes["totalPaidHolidayBonus"],
                this.entryCodes["totalPaidHolidayCompensation"],
                this.entryCodes["totalPaidHolidaySocialSecurity"],
                this.entryCodes["totalPaidHolidayPension"],
                this.entryCodes["totalPaidHolidayUnemployment"],
              ],
              filterType: "set"
            }
          ]
        }
      },
    };
    // create the grid passing in the div to use together with the columns & data we want to use
    this.$timeout(() => {
      const filterModel = this.gridOptions.api.getFilterModel();
      this.gridOptions.api.setFilterModel(null);
      this.gridOptions.api.setRowData(this.data.rows);
      this.gridOptions.api.setColumnDefs(columnDefs);
      this.gridOptions.api.setFilterModel(filterModel);
      this.selectTemplate(this.templateId ?? "holidays");
    });
  }

  private getEntryCodeGroupName(id: string) {
    const name =  this.entryCodeGroups["" + id];
    return name;
  }

  private getEntryCodeName(id: string) {
    const name =  this.entryCodes["" + id];
    return name;
  }

  /**
   * Gets the monthly / quarterly / yearly accounitng row data for the current account.
   * @param refDate Reference date for the period. Please note that even if the date is not the first day of the given period, the entire period is returned.
   * @param periodType Month, quarter, year or a custom period. The custom period requires endDate. Default value is the month.
   * @param endDate End date for the period. Required only for the custom period.
   * @param periodDateKind Period date type: paid at date, salary date or work date.
   * @param workers List of worker official ids.
   * @returns A Promise with result data.
   */
  public queryDataForPeriod(refDate: string, periodType?: PeriodType | "all", endDate?: string, periodDateKind?: PeriodDateKind, workers?: string[]): Promise<AccountingRowReportData> {

    if (periodType === "all") {
      refDate = "2010-01-01";
      endDate = "2100-01-01";
      periodType = PeriodType.Custom;
    }

    return this.reports.getAccountingRowReportData(refDate,periodType,endDate,periodDateKind,workers);
  }

  /**
   * Gets the accounting row data based on given set of calculations.
   * @param calculationIds - Calculations that are the bases for the report.
   * @returns A Promise with result data.
   */
  public queryDataForCalculationIds(calculationIds: string[]): Promise<AccountingRowReportData> {
    return this.reports.getAccountingRowReportDataForCalculationIds(calculationIds);
  }

  private createHolidaysPreset(columnDefs: any) {
    const preset = [];

    for (const def of columnDefs) {
      switch (def.field) {
        case "dimension.workerName":
          preset.push(this.createPresetCol(def.field, false, true));
          break;
        default:
          preset.push(this.createPresetCol(def.field, true));
          break;
      }
    }

    preset.push(this.createPresetCol("amount", false, false, "sum"));

    preset.push(this.createPresetCol("entryCodeGroup", false, false, null, true, 0));

    preset.push(this.createPresetCol("entryCode", false, false, null, true, 1));


    return preset;
  }

  private createPresetCol(colId: string, hide: boolean, rowGroup = false, aggFunc: string = null, pivot = false, pivotIndex: number = null) {
    return {
      aggFunc,
      colId,
      flex: 1,
      hide,
      pinned: null,
      pivot: pivot,
      pivotIndex: pivotIndex,
      rowGroup,
      rowGroupIndex: null,
      sort: null,
      sortIndex: null,
      width: 200,
    };
  }
}
